import { cn } from "@/lib/utils";
import { ModalType, setModal } from "@/store/globalSettingsSlice";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Icon, { ICONS } from "../Core/Icons";

import { useOthers } from "../../../liveblocks.config";
import {
  ChevronRightIcon,
  ExternalLinkIcon,
  HistoryIcon,
  PlayIcon,
  XIcon,
  InfoIcon,
} from "lucide-react";
import { CURSOR_COLORS } from "../LiveblocksCursors";

import ShareModal from "@/components/ShareModal";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import useLayoutStore, {
  LayoutMode,
  LeftTab,
} from "@/store/zustand/layoutStore";
import { useActiveProject, useCanvasId } from "@/utils/utils";
import DesignAgent from "../AI/DesignAgent";
import { useNavigate } from "react-router";
import { ClientSideSuspense } from "@liveblocks/react";
import UserMenu from "./UserMenu";
import { AlertDialog, AlertDialogContent } from "../ui/alert-dialog";
import QRCode from "react-qr-code";
import { useCanvasStore } from "@/store/zustand/canvasStore";
import { ReactFramework } from "@/utils/templateProjects";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@radix-ui/react-dropdown-menu";
import HireHumanModal from "../AssetsPanel/HireHumanModal";
import { useUser } from "@clerk/clerk-react";
import { GitProvider } from "@/store/gitStatusSlice";
const LiveblocksUsers = () => {
  const users = useOthers();
  const hasMoreUsers = users.length > 3;

  return (
    <>
      {users.slice(0, 3).map(({ connectionId, info }) => {
        return (
          <img
            key={connectionId}
            className="h-[28px] w-[28px] rounded-md border-[2px] border-solid"
            style={{
              borderColor: CURSOR_COLORS[connectionId % CURSOR_COLORS.length],
            }}
            src={info.avatar}
            crossOrigin="anonymous"
            data-tooltip-id="tooltip"
            data-tooltip-content={info.name}
          />
        );
      })}
      {hasMoreUsers && (
        <div className="text-2xs font-semibold text-black">
          +{users.length - 3}
        </div>
      )}
    </>
  );
};

const ProjectHeader = () => {
  const { user } = useUser();
  const dispatch = useDispatch();

  const canvasId = useCanvasId();

  const gitStatus = useSelector((state: any) => state.gitStatus.gitStatus);

  const uncommittedChanges = useMemo(() => {
    return (gitStatus?.status?.files || []).length;
  }, [gitStatus]);

  const project = useActiveProject();

  const [showShareModal, setShowShareModal] = useState(false);
  const [showHireHumanModal, setShowHireHumanModal] = useState(false);

  const layoutMode = useLayoutStore((state) => state.layoutMode);
  const leftPanelSize = useLayoutStore((state) => state.leftPanelSize);
  const leftPanelExtensionSize = useLayoutStore(
    (state) => state.leftPanelExtensionSize
  );
  const canvasSize = useLayoutStore((state) => state.canvasSize);
  const expandLeft = useLayoutStore((state) => state.expandLeft);
  const collapseRight = useLayoutStore((state) => state.collapseRight);
  const expandRight = useLayoutStore((state) => state.expandRight);
  const setNewLayout = useLayoutStore((state) => state.setNewLayout);
  const addContextMode = useLayoutStore((state) => state.addContextMode);

  const setExpoQrCodeUrl = useCanvasStore((state) => state.setExpoQrCodeUrl);
  const expoQrCodeUrl = useCanvasStore((state) => state.expoQrCodeUrl);

  const [showExpoQRCodeDialog, setShowExpoQRCodeDialog] = useState(false);

  const canvasIdToDevServerUrl = useSelector(
    (state: any) => state.iframe.canvasIdToDevServerUrl
  );
  const devServerUrl = canvasIdToDevServerUrl[canvasId];

  const navigate = useNavigate();

  /**
   * Open the AI modal when it is needed for adding contxt
   */
  useEffect(() => {
    if (layoutMode === LayoutMode.PRD) {
      if (addContextMode) {
        dispatch(setModal(ModalType.AI));
      } else {
        dispatch(setModal(ModalType.None));
      }
    } else {
      // Always close the modal when switching layout modes
      dispatch(setModal(ModalType.None));
    }
  }, [layoutMode, addContextMode]);

  return (
    <div className="h-11 shrink-0 border-b text-white px-1 flex flex-row items-center justify-between z-[100003] relative">
      <div className="h-full w-full grid grid-cols-3 justify-between ml-0 mr-[8px] my-0">
        <div
          className={cn("flex items-center justify-start pointer-events-auto")}
        >
          <div onClick={() => navigate("/")} className="cursor-pointer">
            <Icon
              icon={ICONS.TEMPO_LOGO_WHITE}
              className="flex grow-0 w-5 h-5 opacity-50 cursor-pointer mr-2 p-2.5 shrink-0"
            />
          </div>
          <div className="h-full flex justify-start items-center flex-1 font-normal">
            <div className="text-xs leading-none gap-1.5 flex flex-row justify-start items-start py-2 px-4 w-full max-w-full truncate">
              {project?.name && (
                <div
                  className="truncate shrink max-w-48 text-muted-foreground"
                  title={project.name}
                >
                  {project.name}
                </div>
              )}
              <ChevronRightIcon
                aria-hidden="true"
                className="w-3 h-3 text-icons-secondary"
              />
              {project?.repoPath && (
                <div
                  className="cursor-pointer text-foreground hover:underline truncate shrink max-w-44"
                  onClick={() => {
                    if (
                      !project?.provider ||
                      project.provider === GitProvider.GITHUB
                    ) {
                      window.open(`https://github.com/${project?.repoPath}`);
                    } else if (project?.provider === GitProvider.GITLAB) {
                      window.open(`https://gitlab.com/${project?.repoPath}`);
                    }
                  }}
                  title={project?.repoPath || ""}
                >
                  {project?.repoPath}
                </div>
              )}
            </div>
          </div>
        </div>
        <div
          className={cn("flex items-center justify-center pointer-events-auto")}
        >
          <Tabs
            className="text-2xs"
            value={layoutMode.toString()}
            onValueChange={(value) => {
              if (value === LayoutMode.Code.toString()) {
                // @ts-expect-error TS2345
                setNewLayout(null, LayoutMode.Code);
                expandLeft();
                collapseRight();
              } else if (value === LayoutMode.PRD.toString()) {
                // @ts-expect-error TS2345
                setNewLayout(null, LayoutMode.PRD);
                expandLeft();
                collapseRight();
              } else {
                // @ts-expect-error TS2345
                setNewLayout(null, LayoutMode.Design);
                expandLeft();
                expandRight();
              }
            }}
          >
            <TabsList className="grid w-full grid-cols-3 py-0 h-7">
              <TabsTrigger
                value={LayoutMode.PRD.toString()}
                className="text-xs"
              >
                PRD
              </TabsTrigger>
              <TabsTrigger
                value={LayoutMode.Design.toString()}
                className="text-xs"
              >
                Design
              </TabsTrigger>
              <TabsTrigger
                value={LayoutMode.Code.toString()}
                className="text-xs flex flex-row items-center justify-center"
              >
                <span>Code</span>
                {uncommittedChanges > 0 && (
                  <div
                    className={cn(
                      "rounded-md bg-primary-0 text-white h-4 text-3xs text-center flex justify-center items-center ml-1 w-4"
                    )}
                  >
                    <span>{uncommittedChanges}</span>
                  </div>
                )}
              </TabsTrigger>
            </TabsList>
          </Tabs>
          {/* Floating at the TOP -> AI & Design Generations */}
          <div
            className={cn(
              "w-full left-0 top-[calc(100%+8px)] absolute flex items-end pointer-events-none justify-center"
            )}
            style={{
              left: `${leftPanelSize + leftPanelExtensionSize}%`,
              right: `${canvasSize + leftPanelSize + leftPanelExtensionSize}%`,
              width: `${canvasSize}%`,
            }}
          >
            <div className="flex flex-col items-center justify-center relative">
              <div className={cn("flex")}>
                <DesignAgent />
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end items-center pointer-events-auto">
          <Button
            size="sm"
            variant={"ghost"}
            className="h-[28px] w-[102px] p-3 hover:text-white mr-1.5 border-radius-md text-nowrap"
            style={{
              border: "1px solid #2F3035",
              color: "transparent",
              background:
                "linear-gradient(90deg, #38C793 0%, #937FF4 50%, #DA41D3 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
            data-tooltip-id="tooltip"
            onClick={(e) => {
              setShowHireHumanModal(true);
            }}
          >
            Hire a Human
          </Button>
          <Button
            size="sm"
            variant={"ghost"}
            className="h-[28px] w-[28px] p-0 text-icons-muted hover:text-white mr-1.5"
            data-tooltip-content={"View history"}
            data-tooltip-id="tooltip"
            onClick={(e) => {
              setNewLayout(LeftTab.History);
            }}
          >
            <HistoryIcon className={cn("w-4 h-4")} />
          </Button>
          <div className="flex items-center">
            <Button
              size="sm"
              disabled={!devServerUrl}
              variant={"ghost"}
              className="h-[28px] w-[28px] p-0 text-icons-muted hover:text-white"
              data-tooltip-content={"View app in new tab"}
              data-tooltip-id="tooltip"
              onClick={(e) => {
                e.preventDefault();
                window.open(devServerUrl, "_blank");
              }}
            >
              <PlayIcon className={cn("w-4 h-4")} />
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild disabled={!devServerUrl}>
                <ChevronRightIcon
                  className={cn(
                    "h-3 w-3 text-icons-muted rotate-90 hover:text-white cursor-pointer",
                    !devServerUrl &&
                      "opacity-50 hover:text-icons-muted cursor-default",
                    project?.framework !== ReactFramework.EXPO && "hidden"
                  )}
                />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56">
                <DropdownMenuItem
                  onClick={(e) => {
                    e.preventDefault();
                    window.open(devServerUrl, "_blank");
                  }}
                  className="cursor-pointer"
                >
                  <ExternalLinkIcon className="w-3 h-3 mr-2" />
                  <span>Preview in new tab</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    // Timeout necessary so that the dropdown closes before the QR code dialog opens
                    setTimeout(() => {
                      setShowExpoQRCodeDialog(true);
                    }, 100);
                  }}
                  className="cursor-pointer"
                >
                  <img
                    src={"/images/expo.svg"}
                    className="w-3 h-3 mr-2 fill-white text-white"
                  />
                  <span>Preview on your phone</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="bg-border w-[1.5px] h-4 ml-1.5 mr-3" />

          <div className={cn("flex grow-0 items-center shrink-0")}>
            <div className="flex flex-row gap-1.5 items-center justify-center mr-2 last:mr-1">
              <ClientSideSuspense fallback={<></>}>
                <LiveblocksUsers />
              </ClientSideSuspense>
              <div
                data-tooltip-id="tooltip"
                data-tooltip-content={`You (${
                  user?.username || user?.primaryEmailAddress?.emailAddress
                })`}
              >
                <UserMenu />
              </div>
            </div>
          </div>
          <div
            className={cn(
              "flex flex-row items-center justify-center px-2 pr-0 gap-3 pointer-events-auto h-full shrink-0"
            )}
          >
            <Button
              variant="primary"
              data-tooltip-id="tooltip"
              size="sm"
              data-tooltip-content={"Share"}
              className={cn("w-[64px] h-[28px] rounded-md bg-background-CTA")}
              onClick={() => setShowShareModal(true)}
            >
              Share
            </Button>
          </div>
          {showShareModal ? (
            <ShareModal closeModal={() => setShowShareModal(false)} />
          ) : null}
          {showHireHumanModal ? (
            <HireHumanModal closeModal={() => setShowHireHumanModal(false)} />
          ) : null}
        </div>
      </div>

      <AlertDialog
        open={showExpoQRCodeDialog}
        onOpenChange={setShowExpoQRCodeDialog}
      >
        <AlertDialogContent className="bg-[#121212] text-white border-none min-w-[400px]">
          <div className="flex justify-end">
            <button
              onClick={() => setShowExpoQRCodeDialog(false)}
              className="text-gray-400 hover:text-white"
            >
              <XIcon className="w-5 h-5" />
            </button>
          </div>

          <div className="flex justify-center mt-2">
            <img src="/images/expo-full.svg" alt="Expo Logo" className="h-12" />
          </div>

          <h2 className="text-2xl font-medium text-center mt-2">
            Preview on your own mobile device
          </h2>

          <div className="flex flex-col items-center mt-2">
            <div className="flex items-center gap-1 text-text-secondary">
              <span className="text-lg">1.</span>
              <span className="text-lg">Install</span>
              <a
                href="https://expo.dev/go"
                target="_blank"
                rel="noopener noreferrer"
                className="text-lg text-primary-n-500 hover:text-primary-n-400"
              >
                Expo Go
              </a>
            </div>

            <div className="flex items-center gap-1 mb-4 text-text-secondary">
              <span className="text-lg">2.</span>
              <span className="text-lg">Scan this QR code</span>
            </div>

            <div className="rounded-lg p-2 flex items-center justify-center border mb-4">
              <div className="flex items-center gap-2 text-gray-300">
                <InfoIcon className="w-5 h-5" />
                <span>
                  Use the code scanner app on iOS or Expo Go on Android
                </span>
              </div>
            </div>

            <div className="flex justify-center p-4 bg-white rounded-md">
              {expoQrCodeUrl && <QRCode value={expoQrCodeUrl} size={200} />}
            </div>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProjectHeader;
